/** spec14 FR6: what a container card shows below its name, from Docker's list answer. */

/** "jellyfin/jellyfin:10.10" → repo and tag; an untagged image is "latest", an id is cut. */
export function imageParts(image: string): { repo: string; tag: string | null } {
  if (/^sha256:[a-f0-9]+$/i.test(image)) return { repo: image.slice(7, 19), tag: null };
  const slash = image.lastIndexOf("/");
  const colon = image.lastIndexOf(":");
  if (colon > slash) return { repo: image.slice(0, colon), tag: image.slice(colon + 1) };
  return { repo: image, tag: "latest" };
}

const DAYS_PER = { week: 7, month: 30, year: 365 } as const;
/** Docker's status texts are short; longer input is cut before parsing. */
const MAX_STATUS = 100;
/**
 * Fully anchored, with bounded numbers and no overlapping parts, so matching stays linear on
 * any input (no ReDoS).
 */
const DURATION =
  /^(?:about )?(\d{1,6}|an?|less than a) (second|minute|hour|day|week|month|year)s?(?: ago)?$/i;
const ENDED = /^(?:Exited|Restarting) \((-?\d{1,5})\) ([^()]{1,60})$/;

/**
 * Docker's relative time ("About an hour", "2 weeks ago", "Less than a second") as a number
 * with days, hours or minutes only: "1 hour", "14 days", "0 minutes". Weeks, months and years
 * become days (Docker's months and years are rounded, so these are too).
 */
export function plainDuration(text: string): string | null {
  const match = DURATION.exec(text.slice(0, MAX_STATUS).trim());
  if (!match?.[1] || !match[2]) return null;
  const amount = match[1].toLowerCase();
  const unit = match[2].toLowerCase();
  if (amount === "less than a" || unit === "second") return "0 minutes";
  let n = /^\d+$/.test(amount) ? Number(amount) : 1;
  let plain = unit;
  if (unit in DAYS_PER) {
    n *= DAYS_PER[unit as keyof typeof DAYS_PER];
    plain = "day";
  }
  return `${n} ${plain}${n === 1 ? "" : "s"}`;
}

/**
 * Docker's status text in parts: "Up 3 days (healthy)" → since "3 days";
 * "Exited (1) 2 hours ago" → since "2 hours", exit code 1.
 */
export function containerFacts(status: string): { since: string | null; exitCode: number | null } {
  const text = status.slice(0, MAX_STATUS);
  if (text.startsWith("Up ")) {
    // Without the "(healthy)" / "(health: starting)" / "(Paused)" at the end.
    let since = text.slice("Up ".length);
    const note = since.lastIndexOf(" (");
    if (note > 0 && since.endsWith(")")) since = since.slice(0, note);
    return { since: plainDuration(since), exitCode: null };
  }
  const ended = ENDED.exec(text);
  if (ended?.[1] && ended[2]) {
    return { since: plainDuration(ended[2]), exitCode: Number(ended[1]) };
  }
  return { since: null, exitCode: null };
}

/** "12 days", "3 hours", "5 minutes": the largest whole unit of `seconds`. */
export function durationOf(seconds: number): string {
  const minutes = Math.max(0, Math.floor(seconds / 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const [n, unit] = days > 0 ? [days, "day"] : hours > 0 ? [hours, "hour"] : [minutes, "minute"];
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
}
